import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import useDebounce from '../Hooks/useDebounce';
import { updateWeezBeeCourseAPI, getWeezBeeCourseApi, getWeezBeeCourseAPI, markSectionAsCompletedAPI } from '../Services/api';
import { SA_updatePublicTemplateAPI } from '../Services/SA_PublicTemplates_api';
import { v4 as uuidv4 } from 'uuid';

/**
 * Course mode constants
 * - EDIT: Admin editing course content
 * - ADMIN_VIEW: Admin previewing course without progress tracking
 * - USER_VIEW: User taking course with progress tracking and sequential navigation
 */
export const COURSE_MODES = {
    EDIT: 'edit',
    ADMIN_VIEW: 'admin_view',
    USER_VIEW: 'user_view'
};

// Create the context
export const CourseEditorContext = createContext();

export const CourseEditorProvider = ({
    children,
    initialCourseData = [],
    signDocId,
    initialTemplates = {},
    isTemplateMode = false,
    templateId = null,
    initialMode = COURSE_MODES.EDIT
}) => {
    // State for course data and selected section
    const [sections, setSections] = useState(initialCourseData);
    const [selectedSection, setSelectedSection] = useState(
        initialCourseData.length > 0 ? initialCourseData[0] : null
    );
    const [isLoading, setIsLoading] = useState(false);
    const [isFetchingCourse, setIsFetchingCourse] = useState(false);
    const [templates, setTemplates] = useState(initialTemplates);

    const [mode, setModeState] = useState(initialMode);

    // User session state - only used in USER_VIEW mode
    // Backend structure: { sectionId: { selectedOptionIds: [...], answeredAt: '...' } }
    // This is the single source of truth for both completion status AND answers
    const [completedSections, setCompletedSections] = useState({});
    const [isUserMode, setIsUserMode] = useState(initialMode === COURSE_MODES.USER_VIEW);

    // State for AI generation
    const [isGeneratingChapter, setIsGeneratingChapter] = useState(false);
    const [generatingChapterId, setGeneratingChapterId] = useState(null);
    const [aiGenerationStatus, setAiGenerationStatus] = useState(null);

    // State for tracking recently created AI chapters for animations
    const [recentlyCreatedAIChapters, setRecentlyCreatedAIChapters] = useState(new Set());
    const [isShowingAIChapterAnimations, setIsShowingAIChapterAnimations] = useState(false);

    // State for tracking changes that need to be sent to the server
    const [pendingChanges, setPendingChanges] = useState(false);
    const [changeCounter, setChangeCounter] = useState(0);

    // ========================================
    // UI State for Course Editor (eliminates prop drilling)
    // ========================================

    /** @type {[boolean, Function]} Fullscreen mode state - independent of mode */
    const [isFullScreen, setIsFullScreenState] = useState(false);

    /** @type {[number, Function]} Canvas scale factor for zoom */
    const [canvasScale, setCanvasScaleState] = useState(1);

    /** @type {[string|null, Function]} Currently selected component ID */
    const [selectedComponentId, setSelectedComponentIdState] = useState(null);

    /** @type {[boolean, Function]} Editor drawer open state */
    const [isEditorDrawerOpen, setIsEditorDrawerOpenState] = useState(false);

    /** @type {[object|null, Function]} Component being edited in drawer */
    const [editingComponent, setEditingComponentState] = useState(null);

    // Debounce the changes to prevent frequent server updates
    const debouncedChangeCounter = useDebounce({value: changeCounter, delay: 500});

    // Update sections when initialCourseData changes
    useEffect(() => {
        if (initialCourseData && initialCourseData.length > 0) {
            setSections(prevSections => {
                // Only update if different to prevent unnecessary re-renders
                if (JSON.stringify(prevSections) !== JSON.stringify(initialCourseData)) {
                    return initialCourseData;
                }
                return prevSections;
            });

            // Only set initial selection if no section is selected yet
            if (!selectedSection) {
                setSelectedSection(initialCourseData[0]);
            }
        }
    }, [initialCourseData]);

    // Update templates when initialTemplates changes
    // Only update on mount or when initialTemplates is actually different
    useEffect(() => {
        if (initialTemplates && Object.keys(initialTemplates).length > 0) {
            setTemplates(prevTemplates => {
                // Only update if different to prevent unnecessary re-renders
                if (JSON.stringify(prevTemplates) !== JSON.stringify(initialTemplates)) {
                    return initialTemplates;
                }
                return prevTemplates;
            });
        }
    }, [initialTemplates]);

    /**
     * Unified fetch function for WeezBeeCourse data
     * - USER_VIEW mode: Fetches from user API (includes completedChapters)
     * - EDIT/ADMIN_VIEW mode: Fetches from admin API (no progress tracking)
     * - Can be called manually to refresh course data (e.g., during AI generation)
     */
    const fetchCourseData = useCallback(async (options = {}) => {
        const { preserveSelection = false } = options;

        // Skip if no signDocId or in template mode
        if (!signDocId || isTemplateMode) {
            return;
        }

        try {
            setIsFetchingCourse(true);

            let response;
            if (mode === COURSE_MODES.USER_VIEW) {
                // User API: includes progress tracking
                response = await getWeezBeeCourseApi({ signDocId });
            } else {
                // Admin API: no progress tracking
                response = await getWeezBeeCourseAPI({ signDocId });
            }

            // Extract course data
            const courseData = response.weezbeeCourse || response;

            if (courseData && courseData.sections) {
                setSections(courseData.sections);

                // Set initial selection only if not preserving or no current selection
                if (!preserveSelection || !selectedSection) {
                    if (courseData.sections.length > 0) {
                        setSelectedSection(courseData.sections[0]);
                    }
                }

                // Extract and set completed sections (USER_VIEW only)
                // Backend returns completedSectionsData object: { sectionId: { selectedOptionIds: [...], answeredAt: '...' } }
                if (mode === COURSE_MODES.USER_VIEW) {
                    const completedSectionsData = response.completedSectionsData || courseData.completedSectionsData || {};

                    // Store the full object from backend - single source of truth
                    setCompletedSections(completedSectionsData);
                }

                return courseData.sections;
            } else {
                console.error('No course sections found in response');
                return [];
            }
        } catch (error) {
            console.error('Error fetching course data:', error);
            return [];
        } finally {
            setIsFetchingCourse(false);
        }
    }, [signDocId, mode, isTemplateMode]);

    // Fetch on mount if no initial course data provided
    useEffect(() => {
        if (initialCourseData.length === 0) {
            fetchCourseData();
        }
    }, [initialCourseData.length, fetchCourseData]);

    // Send updates to the server when debouncedChangeCounter changes
    // We use a ref to track if an update is in progress to prevent overlapping requests
    const updateInProgressRef = useRef(false);

    useEffect(() => {
        const updateServer = async () => {
            // Only proceed if we have changes and no update is currently in progress
            if (debouncedChangeCounter > 0 && pendingChanges && !updateInProgressRef.current) {
                updateInProgressRef.current = true;
                setIsLoading(true);
                try {
                    if (isTemplateMode) {
                        // If in template mode, update the template
                        // Use templateId if available, otherwise use signDocId as the template ID
                        const effectiveTemplateId = templateId || signDocId;
                        await SA_updatePublicTemplateAPI({
                            id: effectiveTemplateId,
                            sections
                        });
                    } else {
                        // Otherwise, update the course
                        await updateWeezBeeCourseAPI({
                            signDocId,
                            sections
                        });
                    }

                    setPendingChanges(false);
                } catch (error) {
                    console.error('Error updating course/template:', error);
                } finally {
                    setIsLoading(false);
                    updateInProgressRef.current = false;
                }
            }
        };

        updateServer();
    }, [debouncedChangeCounter, pendingChanges, sections, signDocId, isTemplateMode, templateId]);

    // Function to update a section
    const updateSection = useCallback((updatedSection) => {
        setSections(prevSections => {
            const index = prevSections.findIndex(section => section.id === updatedSection.id);
            if (index !== -1) {
                const newSections = [...prevSections];
                newSections[index] = updatedSection;
                return newSections;
            }
            return prevSections;
        });
        // Also update selectedSection if it's the one being updated
        setSelectedSection(prevSelected => {
            if (prevSelected && prevSelected.id === updatedSection.id) {
                return updatedSection;
            }
            return prevSelected;
        });
        // Mark that we have pending changes to send to the server
        setPendingChanges(true);
        setChangeCounter(prev => prev + 1);
    }, []);

    // Function to add a new section
    const addSection = useCallback((newSection, afterId = null) => {
        setSections(prevSections => {
            const newSections = [...prevSections];
            if (afterId) {
                const index = newSections.findIndex(section => section.id === afterId);
                newSections.splice(index + 1, 0, newSection);
            } else {
                newSections.push(newSection);
            }
            return newSections;
        });

        // Automatically select the newly added section
        setSelectedSection(newSection);

        // Mark that we have pending changes to send to the server
        setPendingChanges(true);
        setChangeCounter(prev => prev + 1);
    }, []);

    // Function to detect new chapters during AI generation and auto-switch
    const handleAIChapterCreated = useCallback((newChapter) => {
        // Add the new chapter only if it doesn't already exist
        setSections(prevSections => {
            // Check if chapter already exists
            const existingChapterIndex = prevSections.findIndex(section => section.id === newChapter.id);

            if (existingChapterIndex >= 0) {
                // Chapter already exists, update it instead of adding
                const newSections = [...prevSections];
                newSections[existingChapterIndex] = newChapter;
                return newSections;
            } else {
                // Chapter doesn't exist, add it
                return [...prevSections, newChapter];
            }
        });

        // Mark this chapter as recently created by AI for animations
        setRecentlyCreatedAIChapters(prev => new Set([...prev, newChapter.id]));

        // Always auto-switch to the new chapter when it's created by AI
        // This ensures every new AI-generated chapter is immediately selected and visible
        setSelectedSection(newChapter);

        // Trigger AI animations for the new chapter
        setIsShowingAIChapterAnimations(true);

        // Mark that we have pending changes to send to the server
        setPendingChanges(true);
        setChangeCounter(prev => prev + 1);
    }, []);

    // Function to delete a section
    const deleteSection = useCallback((sectionId) => {
        setSections(prevSections => {
            const newSections = prevSections.filter(section => section.id !== sectionId);
            return newSections;
        });

        // If the deleted section was selected, select the first section in the updated list
        if (selectedSection && selectedSection.id === sectionId) {
            setSections(prevSections => {
                setSelectedSection(prevSections.length > 0 ? prevSections[0] : null);
                return prevSections;
            });
        }

        // Mark that we have pending changes to send to the server
        setPendingChanges(true);
        setChangeCounter(prev => prev + 1);
    }, [selectedSection]);

    // Function to duplicate a section
    const duplicateSection = useCallback((sectionId, copyText = '(copy)') => {
        setSections(prevSections => {
            const sectionToDuplicate = prevSections.find(section => section.id === sectionId);
            if (!sectionToDuplicate) {
                return prevSections;
            }

            // Create a deep copy of the section
            const duplicatedSection = {
                ...sectionToDuplicate,
                id: uuidv4(), // Generate new unique ID
                title: `${sectionToDuplicate.title} ${copyText}`,
                // Deep copy templateData if it exists
                templateData: sectionToDuplicate.templateData ? 
                    JSON.parse(JSON.stringify(sectionToDuplicate.templateData)) : 
                    sectionToDuplicate.templateData
            };

            // Find the index of the original section and insert the duplicate right after it
            const originalIndex = prevSections.findIndex(section => section.id === sectionId);
            const newSections = [...prevSections];
            newSections.splice(originalIndex + 1, 0, duplicatedSection);

            return newSections;
        });

        // Mark that we have pending changes to send to the server
        setPendingChanges(true);
        setChangeCounter(prev => prev + 1);
    }, []);

    // Function to move a section
    const moveSection = useCallback((fromIndex, toIndex) => {
        setSections(prevSections => {
            const newSections = [...prevSections];
            const [movedSection] = newSections.splice(fromIndex, 1);
            newSections.splice(toIndex, 0, movedSection);
            return newSections;
        });

        // Mark that we have pending changes to send to the server
        setPendingChanges(true);
        setChangeCounter(prev => prev + 1);
    }, []);

    // Function to move a section up
    const moveSectionUp = useCallback((sectionId) => {
        setSections(prevSections => {
            const index = prevSections.findIndex(section => section.id === sectionId);
            if (index > 0) {
                const newSections = [...prevSections];
                const temp = newSections[index];
                newSections[index] = newSections[index - 1];
                newSections[index - 1] = temp;
                return newSections;
            }
            return prevSections;
        });

        // Mark that we have pending changes to send to the server
        setPendingChanges(true);
        setChangeCounter(prev => prev + 1);
    }, []);

    // Function to move a section down
    const moveSectionDown = useCallback((sectionId) => {
        setSections(prevSections => {
            const index = prevSections.findIndex(section => section.id === sectionId);
            if (index < prevSections.length - 1) {
                const newSections = [...prevSections];
                const temp = newSections[index];
                newSections[index] = newSections[index + 1];
                newSections[index + 1] = temp;
                return newSections;
            }
            return prevSections;
        });

        // Mark that we have pending changes to send to the server
        setPendingChanges(true);
        setChangeCounter(prev => prev + 1);
    }, []);

    // Function to select a section
    const selectSection = useCallback((sectionId) => {
        const section = sections.find(section => section.id === sectionId);
        if (section) {
            // Check if this is a recently created AI chapter
            if (recentlyCreatedAIChapters.has(sectionId)) {
                setIsShowingAIChapterAnimations(true);

                // Remove the chapter from recently created set after triggering animations
                // This ensures animations only play once per chapter
                setTimeout(() => {
                    setRecentlyCreatedAIChapters(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(sectionId);
                        return newSet;
                    });
                    setIsShowingAIChapterAnimations(false);
                }, 5000); // Clear after 5 seconds to allow animations to complete
            } else {
                // Not a recently created AI chapter, ensure animations are off
                setIsShowingAIChapterAnimations(false);
            }

            setSelectedSection(section);
        } else {
            console.error('CourseEditorContext: Section not found with ID:', sectionId);
        }
    }, [sections, recentlyCreatedAIChapters]);

    // Function to set the generating chapter state
    const setGeneratingChapter = useCallback((isGenerating, chapterId = null) => {
        setIsGeneratingChapter(isGenerating);
        setGeneratingChapterId(chapterId);
    }, []);

    // Function to select the most recently created chapter
    const selectLastCreatedChapter = useCallback(() => {
        if (sections.length > 0) {
            // Since new chapters are added to the end of the array,
            // the most recently created chapter is the last one
            const lastChapter = sections[sections.length - 1];
            setSelectedSection(lastChapter);
        }
    }, [sections]);

    /**
     * Check if section is accessible based on mode and completion status
     * @param {string} sectionId - Section to check
     * @returns {boolean} True if section can be accessed
     */
    const isSectionAccessible = useCallback((sectionId) => {
        // EDIT and ADMIN_VIEW: all sections accessible
        if (mode === COURSE_MODES.EDIT || mode === COURSE_MODES.ADMIN_VIEW) {
            return true;
        }

        // USER_VIEW: sequential restriction
        if (mode === COURSE_MODES.USER_VIEW) {
            const sectionIndex = sections.findIndex(s => s.id === sectionId);

            // First section always accessible
            if (sectionIndex === 0) {
                return true;
            }

            // Check if previous section completed (using object structure)
            const previousSection = sections[sectionIndex - 1];
            const isAccessible = previousSection && completedSections[previousSection.id] ? true : false;

            return isAccessible;
        }

        return true;
    }, [mode, sections, completedSections]);

    /**
     * Mark a chapter/section as completed (USER_VIEW mode only)
     * Calls the backend API and updates local state with the response
     * Stores question answers for sections with questions
     *
     * @param {string} chapterId - The ID of the chapter to mark as completed
     * @param {Array<string>|null} [selectedOptionIds=null] - Optional array of selected option IDs for question sections
     * @returns {Promise<{success: boolean, allCompleted: boolean, completedSections: object}>}
     */
    const markChapterCompleted = useCallback(async (chapterId, selectedOptionIds = null) => {
        // CRITICAL: Only track completion in USER_VIEW mode
        if (mode !== COURSE_MODES.USER_VIEW) {
            return { success: false, allCompleted: false, completedSections };
        }

        if (!chapterId || !signDocId) {
            console.error('Missing chapterId or signDocId for completion tracking');
            return { success: false, allCompleted: false, completedSections };
        }

        // Skip if chapter already completed (using object structure)
        if (completedSections[chapterId]) {
            return { success: true, allCompleted: false, completedSections };
        }

        try {
            const response = await markSectionAsCompletedAPI({
                signDocId,
                sectionId: chapterId,
                selectedOptionIds
            });

            if (response && response.completedSections) {
                // Backend returns full completedSections object
                // Update local state with server response
                setCompletedSections(response.completedSections);

                return {
                    success: true,
                    allCompleted: response.allCompleted || false,
                    completedSections: response.completedSections
                };
            }

            return { success: false, allCompleted: false, completedSections };
        } catch (error) {
            console.error('Error marking chapter as completed:', error);

            // Return unchanged state - user should retry or refresh to get accurate completion status
            return {
                success: false,
                allCompleted: false,
                completedSections: completedSections // Return unchanged state
            };
        }
    }, [mode, signDocId, completedSections]);


    // ========================================
    // Fullscreen Utilities
    // ========================================

    /**
     * Check if browser is currently in native fullscreen mode
     * @returns {boolean} True if browser is in fullscreen
     */
    const isBrowserInFullscreen = useCallback(() => {
        return !!(
            document.fullscreenElement ||
            document.webkitFullscreenElement ||
            document.mozFullScreenElement ||
            document.msFullscreenElement
        );
    }, []);

    /**
     * Request browser native fullscreen on the course element
     * @returns {Promise<void>} Resolves when fullscreen is entered
     */
    const enterBrowserFullscreen = useCallback(async () => {
        const courseElement = document.querySelector('.WeezBeeCourse');
        if (!courseElement) return;

        try {
            const fullscreenPromise = courseElement.requestFullscreen?.() ||
                courseElement.webkitRequestFullscreen?.() ||
                courseElement.mozRequestFullScreen?.() ||
                courseElement.msRequestFullscreen?.();

            if (fullscreenPromise?.then) {
                await fullscreenPromise;
            }
        } catch (err) {
            console.warn('Fullscreen request failed:', err.message);
            throw err;
        }
    }, []);

    /**
     * Exit browser native fullscreen
     */
    const exitBrowserFullscreen = useCallback(() => {
        if (!isBrowserInFullscreen()) return;

        try {
            if (document.exitFullscreen) {
                document.exitFullscreen().catch(err => {
                    console.warn('Exit fullscreen failed:', err.message);
                });
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.mozCancelFullScreen) {
                document.mozCancelFullScreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
        } catch (err) {
            console.warn('Exit fullscreen error:', err.message);
        }
    }, [isBrowserInFullscreen]);

    // ========================================
    // UI State Setter Functions
    // ========================================

    /**
     * Set fullscreen mode - handles both entering and exiting
     * - Enter: Requests browser fullscreen, sets React state on success
     * - Exit: Sets React state immediately, then exits browser fullscreen
     * @param {boolean} value - Whether to enable fullscreen mode
     */
    const setFullScreen = useCallback((value) => {
        if (value) {
            // Enter fullscreen: request browser fullscreen first, set React state on success
            requestAnimationFrame(async () => {
                try {
                    await enterBrowserFullscreen();
                    setIsFullScreenState(true);
                } catch {
                    // Browser fullscreen failed - don't set React state
                }
            });
        } else {
            // Exit fullscreen: set React state immediately, then exit browser fullscreen
            setIsFullScreenState(false);
            exitBrowserFullscreen();
        }
    }, [enterBrowserFullscreen, exitBrowserFullscreen]);

    /**
     * Set canvas scale factor
     * @param {number} value - Scale factor (typically 0.5 to 2.0)
     */
    const setCanvasScale = useCallback((value) => {
        setCanvasScaleState(value);
    }, []);

    /**
     * Select a component by ID
     * @param {string|null} id - Component ID to select, or null to deselect
     */
    const selectComponent = useCallback((id) => {
        setSelectedComponentIdState(id);
    }, []);

    /**
     * Open the editor drawer with a component
     * @param {object} component - Component data to edit
     */
    const openEditorDrawer = useCallback((component) => {
        setEditingComponentState(component);
        setIsEditorDrawerOpenState(true);
    }, []);

    /**
     * Close the editor drawer and clear editing component
     */
    const closeEditorDrawer = useCallback(() => {
        setIsEditorDrawerOpenState(false);
        // Keep editingComponent for a moment to allow closing animation
        setTimeout(() => {
            setEditingComponentState(null);
        }, 300);
    }, []);

    /**
     * Update the editing component data (for syncing with component changes)
     * @param {object} component - Updated component data
     */
    const updateEditingComponent = useCallback((component) => {
        setEditingComponentState(component);
    }, []);

    /**
     * Set mode state with automatic fullscreen handling
     * - Entering ADMIN_VIEW or USER_VIEW → fullscreen turns ON
     * - Exiting to EDIT mode → fullscreen turns OFF
     * - In EDIT mode → fullscreen can be toggled independently via setFullScreen
     * @param {string} newMode - The mode to set (COURSE_MODES.EDIT, ADMIN_VIEW, or USER_VIEW)
     */
    const setMode = useCallback((newMode) => {
        setModeState(newMode);

        // Handle fullscreen: both view modes require fullscreen
        if (newMode === COURSE_MODES.ADMIN_VIEW || newMode === COURSE_MODES.USER_VIEW) {
            setFullScreen(true);
        } else if (newMode === COURSE_MODES.EDIT) {
            setFullScreen(false);
        }

        // Update user mode flag for quick checks
        setIsUserMode(newMode === COURSE_MODES.USER_VIEW);

    }, [mode, setFullScreen]);

    // ========================================
    // UI Side Effects
    // ========================================

    /**
     * Body class effect for fullscreen mode
     * Adds/removes 'weezbee-course-fullscreen' class on document.body
     * This hides AppSideMenu when in fullscreen mode
     */
    useEffect(() => {
        if (isFullScreen) {
            document.body.classList.add('weezbee-course-fullscreen');
        } else {
            document.body.classList.remove('weezbee-course-fullscreen');
        }

        // Cleanup on unmount
        return () => {
            document.body.classList.remove('weezbee-course-fullscreen');
        };
    }, [isFullScreen]);

    /**
     * Sync React fullscreen state with browser's native fullscreen state.
     * When browser exits fullscreen (via ESC or other means), update React state.
     * This prevents the "scaled fullscreen" state when browser fullscreen is off.
     *
     * CRITICAL: USER_VIEW mode should NEVER transition to EDIT mode, even when exiting fullscreen.
     * Users can exit fullscreen but stay in USER_VIEW mode (read-only).
     * Only ADMIN_VIEW mode transitions to EDIT mode when exiting fullscreen.
     */
    useEffect(() => {
        const handleFullscreenChange = () => {
            // If browser exited fullscreen but React state is still true, sync them
            if (!isBrowserInFullscreen() && isFullScreen) {
                setIsFullScreenState(false);

                // CRITICAL: Only ADMIN_VIEW transitions to EDIT when exiting fullscreen
                // USER_VIEW stays in USER_VIEW (read-only, no editing allowed)
                if (mode === COURSE_MODES.ADMIN_VIEW) {
                    setModeState(COURSE_MODES.EDIT);
                }
            }
        };

        // Listen to all fullscreen change event variants
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, [isFullScreen, mode, isBrowserInFullscreen]);

    // Create the context value object
    const contextValue = {
        // Course data state
        sections,
        selectedSection,
        signDocId, // Add signDocId to context
        isLoading,
        setIsLoading,
        isFetchingCourse,
        isGeneratingChapter,
        generatingChapterId,
        aiGenerationStatus,
        setAiGenerationStatus,
        isShowingAIChapterAnimations,
        recentlyCreatedAIChapters,
        templates,
        isTemplateMode,
        templateId,

        // Course data functions
        updateSection,
        addSection,
        deleteSection,
        duplicateSection,
        moveSection,
        moveSectionUp,
        moveSectionDown,
        selectSection,
        setGeneratingChapter,
        selectLastCreatedChapter,
        handleAIChapterCreated,
        fetchCourseData,

        // Mode state
        mode,
        setMode,

        // User session state (for USER_VIEW mode)
        // completedSections is now an object: { sectionId: { selectedOptionIds: [...], answeredAt: '...' } }
        completedSections,
        setCompletedSections,
        isUserMode,
        isSectionAccessible,
        markChapterCompleted,

        // UI State (eliminates prop drilling)
        isFullScreen,
        canvasScale,
        selectedComponentId,
        isEditorDrawerOpen,
        editingComponent,

        // UI State Functions
        setFullScreen,
        setCanvasScale,
        selectComponent,
        openEditorDrawer,
        closeEditorDrawer,
        updateEditingComponent,
    };

    return (
        <CourseEditorContext.Provider value={contextValue}>
            {children}
        </CourseEditorContext.Provider>
    );
};
